import { useEffect, useRef, useMemo } from "react";
import {
  getBoardColumns,
  getProperties,
} from "../services/mondayApiService";
import { 
  PROPERTIES_BOARD_ID,
  PROPERTY_KEYBOX_COLUMN_ID,
  PROPERTY_ALARM_COLUMN_ID,
  PROPERTY_DOOR_GATE_CODE_COLUMN_ID,
  PROPERTY_ADDRESS_COLUMN_ID,
} from "../constants/columnIds";
import { useMondayContext } from "./useMondayContext";
import { parseColumnValue, parseLocationColumn } from "../utils/dataParsingUtils";

/**
 * useProperties - Hook to fetch and manage properties data
 * Implements caching: checks context first, only fetches if data doesn't exist
 */
export function useProperties() {
  const {
    propertyKeyMap,
    availableProperties,
    propertyDetailsMap,
    updatePropertyKeyMap,
    updateAvailableProperties,
    updatePropertyDetailsMap,
  } = useMondayContext();

  const isFetchingKeys = useRef(false);
  const isFetchingProperties = useRef(false);
  const hasFetchedKeys = useRef(false);
  const hasFetchedProperties = useRef(false);
  const hasFetchedPropertyDetails = useRef(false);

  // Memoize whether we have keys to avoid object reference issues
  const propertyKeysCount = Object.keys(propertyKeyMap).length;
  const hasPropertyKeys = propertyKeysCount > 0;

  // Fetch Key # column from Properties board (only once per session)
  useEffect(() => {
    // Check cache first - if data exists, return early
    if (hasPropertyKeys || hasFetchedKeys.current) {
      hasFetchedKeys.current = true;
      return; // Data already cached, no need to fetch
    }

    // Prevent duplicate fetches
    if (isFetchingKeys.current) {
      return;
    }

    const fetchPropertyKeys = async () => {
      isFetchingKeys.current = true;
      try {
        const propertiesBoardId = PROPERTIES_BOARD_ID;
        
        // Fetch board columns to find Key # column
        const boardsResult = await getBoardColumns({ boardIds: [propertiesBoardId] });
        const board = boardsResult?.[0];
        const columns = board?.columns || [];
        
        // Find Key # column - search by title (case insensitive)
        const keyColumn = columns.find(
          (col) => {
            const titleLower = col.title?.toLowerCase() || "";
            return titleLower.includes("key") || titleLower === "key #";
          }
        );
        
        if (!keyColumn) {
          isFetchingKeys.current = false;
          return; // Key # column not found
        }
        
        // Fetch properties with Key # values (only fetch once with the needed column)
        const propertyItemsWithKeys = await getProperties({
          boardId: propertiesBoardId,
          keyColumnId: keyColumn.id,
        });
        
        // Create map of property ID -> Key # value
        const keyMap = {};
        propertyItemsWithKeys.forEach((item) => {
          const keyCol = item.column_values?.find((c) => c.id === keyColumn.id);
          const keyValue = keyCol?.text || keyCol?.value || null;
          if (item.id && keyValue) {
            keyMap[item.id] = keyValue;
          }
        });
        
        // Store in context cache
        updatePropertyKeyMap(keyMap);
        hasFetchedKeys.current = true;
      } catch (err) {
        console.error("Error fetching property keys:", err);
        // Allow retry on error
      } finally {
        isFetchingKeys.current = false;
      }
    };
    
    fetchPropertyKeys();
  }, [propertyKeysCount, updatePropertyKeyMap]);

  // Check if we have properties to avoid array reference issues
  const propertiesCount = availableProperties.length;
  const hasProperties = propertiesCount > 0;
  const propertyDetailsCount = Object.keys(propertyDetailsMap).length;
  const hasPropertyDetails = propertyDetailsCount > 0;

  // Fetch properties list and details in a single call (only once per session)
  useEffect(() => {
    // Check cache first - if both data exists, return early
    if ((hasProperties && hasPropertyDetails) || (hasFetchedProperties.current && hasFetchedPropertyDetails.current)) {
      hasFetchedProperties.current = true;
      hasFetchedPropertyDetails.current = true;
      return; // Data already cached, no need to fetch
    }

    // Prevent duplicate fetches
    if (isFetchingProperties.current) {
      return;
    }

    const fetchPropertiesAndDetails = async () => {
      isFetchingProperties.current = true;
      try {
        const propertiesBoardId = PROPERTIES_BOARD_ID;
        
        // Fetch all properties with detail columns in a single call
        const propertyItems = await getProperties({
          boardId: propertiesBoardId,
          columnIds: [
            PROPERTY_KEYBOX_COLUMN_ID,
            PROPERTY_ALARM_COLUMN_ID,
            PROPERTY_DOOR_GATE_CODE_COLUMN_ID,
            PROPERTY_ADDRESS_COLUMN_ID,
          ],
        });
        
        // Create list of properties for dropdown: {id, name}
        const propertiesList = propertyItems
          .filter(item => item.name && item.id)
          .map(item => ({
            id: item.id,
            name: item.name,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        
        // Create map of property ID -> { keybox, alarm, doorGateCode, location }
        const detailsMap = {};
        propertyItems.forEach((item) => {
          if (item.id && item.column_values) {
            const keybox = parseColumnValue(item.column_values, PROPERTY_KEYBOX_COLUMN_ID) || "";
            const alarm = parseColumnValue(item.column_values, PROPERTY_ALARM_COLUMN_ID) || "";
            const doorGateCode = parseColumnValue(item.column_values, PROPERTY_DOOR_GATE_CODE_COLUMN_ID) || "";
            const location = parseLocationColumn(item.column_values, PROPERTY_ADDRESS_COLUMN_ID);
            
            detailsMap[item.id] = {
              keybox: keybox.trim(),
              alarm: alarm.trim(),
              doorGateCode: doorGateCode.trim(),
              location: location, // { lat, lng } or null
            };
          }
        });
        
        // Store both in context cache
        updateAvailableProperties(propertiesList);
        updatePropertyDetailsMap(detailsMap);
        hasFetchedProperties.current = true;
        hasFetchedPropertyDetails.current = true;
      } catch (err) {
        console.error("Error fetching properties:", err);
        // Allow retry on error
      } finally {
        isFetchingProperties.current = false;
      }
    };
    
    fetchPropertiesAndDetails();
  }, [propertiesCount, propertyDetailsCount, updateAvailableProperties, updatePropertyDetailsMap]);

  return {
    propertyKeyMap,
    availableProperties,
    propertyDetailsMap,
  };
}
